import { useState } from "react";
import { motion } from "framer-motion";
import { Menu, Bell, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCustomer } from "@/lib/customer-context";
import { useNavigation } from "@/hooks/use-navigation";
import { Link } from "wouter";

interface CustomerHeaderProps {
  onMenuClick: () => void;
  topOffset?: boolean;
}

export function CustomerHeader({ onMenuClick, topOffset = false }: CustomerHeaderProps) {
  const { customer, logout } = useCustomer();
  const [isScrolled, setIsScrolled] = useState(false);

  // Track scroll for backdrop effect
  useState(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  });

  const initials = customer?.firstName && customer?.lastName
    ? `${customer.firstName[0]}${customer.lastName[0]}`
    : customer?.email?.[0]?.toUpperCase() || "U";

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed left-0 right-0 z-40 transition-all duration-300 ${
        topOffset ? "top-[44px]" : "top-0"
      } ${isScrolled
          ? "bg-background/95 backdrop-blur-lg border-b shadow-sm"
          : "bg-transparent"
        }`}
    >
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        {/* Left: Menu (Mobile Only) */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onMenuClick}
          className="md:hidden"
        >
          <Menu className="h-6 w-6" />
        </Button>

        {/* Desktop: Empty spacer to maintain layout */}
        <div className="hidden md:block w-10" />

        {/* Center spacer */}
        <div />

        {/* Right: Notifications & Profile */}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="relative">
            <Bell className="h-5 w-5" />
            <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={customer?.avatarUrl || undefined} />
                  <AvatarFallback>{initials}</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium">
                    {customer?.firstName} {customer?.lastName}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {customer?.phone}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <Link href="/portal/account">
                <DropdownMenuItem>
                  <User className="h-4 w-4 mr-2" />
                  Account Settings
                </DropdownMenuItem>
              </Link>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-600">
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </motion.header>
  );
}
